import { find } from 'lodash';
import { getOffset } from '@helpers/mouseUtils';
import {
  memo,
  MouseEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import TimelineRangeEditorPopup from '../TimelineRangeEditorPopup';
import { mapRender } from './mapRender';
import { renderer } from './renderer';
import { BackgroundBlurPropTypes } from './BackgroundBlurPropTypes';

import {
  actionBackgroundBlurSlice,
  actionResetBackgroundBlurSlice,
  selectFrameRate,
} from '@common-modules/mediaDetails';

import { useDispatch, useSelector } from 'react-redux';
import { BackgroundBlurSlice } from '@common/state/modules/mediaDetails/store.models';
import { frameAlignStartTimeMs, frameAlignStopTimeMs } from '@utils';
import {
  AudioEditType,
  BlurLevel,
} from '@common/state/modules/mediaDetails/models';

function getClickedMediaTime(
  evt: MouseEvent,
  currentTarget: Element,
  startWindowMs: number,
  stopWindowMs: number
): number {
  const offset = getOffset(evt);
  const clientRect = currentTarget.getBoundingClientRect();
  const offsetRatio = offset.x / clientRect.width;
  const range = stopWindowMs - startWindowMs;
  return Math.floor(startWindowMs + offsetRatio * range);
}

export enum BACKGROUND_BLUR_ACTION_TYPE {
  ADD = 'ADD',
  UPDATE = 'UPDATE',
}

const BackgroundBlur = (
  props: BackgroundBlurPropTypes & { containerWidth?: number }
) => {
  const {
    mediaDuration,
    list: backgroundBlurEdits,
    height,
    rolePermissions,
    containerWidth,
  } = props;
  const dispatch = useDispatch();

  const ref = useRef<HTMLDivElement>(null);

  const [afterFirstRender, setAfterFirstRender] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [editMode, setEditMode] = useState<BACKGROUND_BLUR_ACTION_TYPE>(
    BACKGROUND_BLUR_ACTION_TYPE.ADD
  );
  const [timeRange, setTimeRange] = useState<BackgroundBlurSlice>([
    0,
    0,
    undefined,
  ]);
  const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0 });
  const [sliceClicked, setSliceClicked] = useState<
    BackgroundBlurSlice | undefined
  >([0, 0, undefined]);
  const [allowMove, setAllowMove] = useState<boolean>(false);
  const [timeClickMs, setTimeClickMs] = useState<number>(0);

  const { render, destroy } = useMemo(
    () => renderer(ref, (layer) => mapRender(layer)),
    [afterFirstRender, containerWidth] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useLayoutEffect(() => render(props, timeRange, height));

  const videoFrameRate = useSelector(selectFrameRate);

  useEffect(() => {
    setAfterFirstRender(true);
    return () => destroy();
  }, [destroy]);

  const onSetRangeTime = (
    start: number,
    end: number,
    options: {
      type?: AudioEditType; // BTODO would like to avoid needing this to compile
      blurLevel?: BlurLevel;
    }
  ) => {
    dispatch(
      actionBackgroundBlurSlice({
        slice: [
          start,
          end,
          options.blurLevel
            ? { blurLevel: options.blurLevel }
            : { blurLevel: 5 },
        ], // BTODO we never actually should get where without blurLevel passed in
        reset:
          sliceClicked && end !== sliceClicked[0] ? [sliceClicked] : undefined, // if editing an existing slice will need to reset original region
        actionType: editMode,
      })
    );

    handleManualRedactClose();
  };

  const handleResetSlice = (start: number, end: number) => {
    dispatch(actionResetBackgroundBlurSlice([[start, end, undefined]]));
    handleManualRedactClose();
  };

  const handleManualRedactClose = () => {
    setIsOpen(false);
    setTimeRange([0, 0, undefined]);
  };

  const onMouseUp = () => {
    setAllowMove(false);
  };

  const onMouseDown = (evt: MouseEvent) => {
    if (!rolePermissions) {
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }
    const { nativeEvent, currentTarget } = evt;
    // Time click (ms)
    const { startWindowMs, stopWindowMs } = props;
    const clickedMediaTime = getClickedMediaTime(
      evt,
      currentTarget,
      startWindowMs,
      stopWindowMs
    );

    // Find slice was clicked
    const sliceClickedExisted = find(
      backgroundBlurEdits,
      ([s, e]) => clickedMediaTime >= s && clickedMediaTime <= e
    );

    // upon first mouse click will show selection highlight on existing slice (if exists)
    if (sliceClickedExisted) {
      setEditMode(BACKGROUND_BLUR_ACTION_TYPE.UPDATE);
      setSliceClicked(sliceClickedExisted);
      setTimeRange(sliceClickedExisted);
    } else {
      setEditMode(BACKGROUND_BLUR_ACTION_TYPE.ADD);
      let newSlice: BackgroundBlurSlice = [
        clickedMediaTime,
        Math.min(clickedMediaTime + 1000, mediaDuration * 1000),
        undefined,
      ];
      if (videoFrameRate) {
        newSlice = [
          Math.ceil(frameAlignStartTimeMs(newSlice[0], videoFrameRate)),
          Math.floor(frameAlignStopTimeMs(newSlice[1], videoFrameRate)),
          newSlice[2],
        ];
      }

      setSliceClicked(newSlice);
      setTimeRange(newSlice);
    }

    setTimeClickMs(clickedMediaTime);
    setAnchorPosition({
      top: currentTarget.getBoundingClientRect().top - 215, // set high enough so doesn't block view of timeline (subtrack form height and 58?)
      left: nativeEvent.clientX,
    });

    setIsOpen(true);

    setAllowMove(true);
  };

  const onChangeTimeRange = (time: number) => {
    let newTimeRange: BackgroundBlurSlice;
    if (time < timeClickMs) {
      newTimeRange = [time, timeClickMs, timeRange[2]];
    } else {
      newTimeRange = [timeClickMs, time, timeRange[2]];
    }

    if (videoFrameRate) {
      newTimeRange = [
        Math.ceil(frameAlignStartTimeMs(newTimeRange[0], videoFrameRate)),
        Math.floor(frameAlignStopTimeMs(newTimeRange[1], videoFrameRate)),
        newTimeRange[2],
      ];
    }

    setTimeRange(newTimeRange);
    return newTimeRange;
  };

  const onMouseMove = (evt: MouseEvent) => {
    if (!allowMove) {
      return;
    }

    const { currentTarget } = evt;

    const { startWindowMs, stopWindowMs } = props;
    const clickedMediaTime = getClickedMediaTime(
      evt,
      currentTarget,
      startWindowMs,
      stopWindowMs
    );

    setSliceClicked(undefined); // reset slice click since no longer editing existing slice

    onChangeTimeRange(clickedMediaTime);
  };

  const onMouseLeave = () => {
    // setAllowMove(false); // this if accidently move mouse outside the timeline the time range finalizes
  };

  return (
    <>
      <div
        id="background-blur-container"
        data-testid="timeline-background-blur"
        ref={ref}
        onMouseUp={onMouseUp}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        style={allowMove ? { zIndex: 102 } : {}}
      />
      <TimelineRangeEditorPopup
        minMs={0}
        maxMs={mediaDuration * 1000}
        timeRange={timeRange}
        anchorPosition={anchorPosition}
        isOpen={isOpen}
        hideGroupButton={allowMove}
        onCancel={handleManualRedactClose}
        onSetRangeTime={onSetRangeTime}
        handleResetSlice={handleResetSlice}
        isBlurForm
        editMode={editMode}
      />
    </>
  );
};

export default memo(BackgroundBlur);
